import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { S } from '../utils/styles';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [families, setFamilies] = useState([]);
  const [search, setSearch] = useState('');
  const [familyFilter, setFamilyFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showFamilyMgr, setShowFamilyMgr] = useState(false);
  const [editProd, setEditProd] = useState(null);
  const [form, setForm] = useState({ code:'', name:'', family:'', colour:'', notes:'' });
  const [newFamily, setNewFamily] = useState('');
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [familyTemplateChoice, setFamilyTemplateChoice] = useState({});
  const [assigningFamily, setAssigningFamily] = useState(null);
  const [showTemplateMgr, setShowTemplateMgr] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(null);

  useEffect(() => { load(); loadTemplates(); }, []);

  const loadTemplates = () => api.get('/stage-templates').then(r => setTemplates(r.data)).catch(()=>{});

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete the template "${t.name}"? This cannot be undone.`)) return;
    setDeletingTemplate(t.id);
    try {
      await api.delete(`/stage-templates/${t.id}`);
      loadTemplates();
    } catch (e) {
      alert(e.response?.data?.error || 'Error deleting template — it may still be assigned to a family');
    } finally {
      setDeletingTemplate(null);
    }
  };

  const assignFamilyTemplate = async (fam) => {
    const templateId = familyTemplateChoice[fam] || '';
    setAssigningFamily(fam);
    try {
      const { data } = await api.put(`/products/family/${encodeURIComponent(fam)}/stage-template`, { stage_template_id: templateId || null });
      alert(`Applied to ${data.updated_count} product(s) in "${fam}".`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Error assigning template');
    } finally {
      setAssigningFamily(null);
    }
  };

  const load = () => {
    api.get('/products').then(r => {
      setProducts(r.data);
      const fams = [...new Set(r.data.map(p => p.family).filter(Boolean))].sort();
      setFamilies(fams);
    }).catch(()=>{});
  };

  const filtered = products.filter(p =>
    (p.code||'').toLowerCase().includes(search.toLowerCase()) ||
    (p.name||'').toLowerCase().includes(search.toLowerCase()) ||
    (p.family||'').toLowerCase().includes(search.toLowerCase())
  ).filter(p => !familyFilter || p.family === familyFilter);

  const grouped = filtered.reduce((acc, p) => {
    const f = p.family || 'Other';
    if (!acc[f]) acc[f] = [];
    acc[f].push(p);
    return acc;
  }, {});

  const openAdd = () => { setForm({ code:'', name:'', family: families[0]||'', colour:'', notes:'' }); setEditProd(null); setShowAdd(true); };
  const openEdit = (p) => { setForm({ code:p.code, name:p.name, family:p.family||'', colour:p.colour||'', notes:p.notes||'' }); setEditProd(p); setShowAdd(true); };

  const save = async () => {
    if (!form.code || !form.name) return alert('Code and name required');
    setSaving(true);
    try {
      if (editProd) await api.put(`/products/${editProd.id}`, form);
      else await api.post('/products', form);
      load(); setShowAdd(false);
    } catch(e) { alert(e.response?.data?.error || 'Error saving product'); }
    finally { setSaving(false); }
  };

  const deleteProd = async (p) => {
    if (!window.confirm(`Delete product ${p.code}? This cannot be undone.`)) return;
    try { await api.delete(`/products/${p.id}`); load(); }
    catch(e) { alert(e.response?.data?.error || 'Cannot delete — may be used in orders'); }
  };

  const addFamily = () => {
    if (!newFamily.trim()) return;
    if (!families.includes(newFamily.trim())) setFamilies(f => [...f, newFamily.trim()].sort());
    setNewFamily('');
  };

  const deleteFamily = (fam) => {
    const inUse = products.some(p => p.family === fam);
    if (inUse) return alert(`Cannot delete "${fam}" — ${products.filter(p=>p.family===fam).length} products use it. Reassign them first.`);
    setFamilies(f => f.filter(x => x !== fam));
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <h1 style={{ fontSize:22, fontWeight:600, margin:0 }}>Products</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setShowFamilyMgr(true)} style={S.btnSecondary}>Manage families</button>
          <button onClick={() => setShowTemplateMgr(true)} style={S.btnSecondary}>Manage templates</button>
          <button onClick={openAdd} style={S.btnPrimary}>+ Add product</button>
        </div>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." style={{ ...S.input, width:260 }} />
        <select value={familyFilter} onChange={e => setFamilyFilter(e.target.value)} style={{ ...S.input, width:200 }}>
          <option value="">All families</option>
          {families.map(f => <option key={f}>{f}</option>)}
        </select>
      </div>

      {Object.entries(grouped).map(([fam, prods]) => (
        <div key={fam} style={{ marginBottom:18 }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#9ca3af', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>{fam} ({prods.length})</div>
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['Code','Name','Family','Colour/Finish','Notes',''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {prods.map(p => (
                  <tr key={p.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ ...S.td, fontWeight:700, color:'#3b82f6' }}>{p.code}</td>
                    <td style={S.td}>{p.name}</td>
                    <td style={{ ...S.td, color:'#6b7280' }}>{p.family||'—'}</td>
                    <td style={{ ...S.td, color:'#6b7280' }}>{p.colour||'—'}</td>
                    <td style={{ ...S.td, color:'#9ca3af', fontSize:12 }}>{p.notes||'—'}</td>
                    <td style={S.td}>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={() => openEdit(p)} style={{ ...S.btnSm, background:'#f3f4f6' }}>Edit</button>
                        <button onClick={() => deleteProd(p)} style={{ ...S.btnSm, background:'#fef2f2', color:'#ef4444' }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {filtered.length === 0 && <div style={{ textAlign:'center', color:'#9ca3af', padding:40 }}>No products found.</div>}

      {/* Add/Edit Modal */}
      {showAdd && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:500 }}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:20 }}>{editProd ? 'Edit product' : 'Add product'}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
              <div><label style={S.label}>Product code *</label><input value={form.code} onChange={e => setForm(p=>({...p,code:e.target.value.toUpperCase()}))} style={S.input} placeholder="e.g. RL, IGBFIX" /></div>
              <div><label style={S.label}>Product name *</label><input value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} style={S.input} placeholder="e.g. Rubber Lined Pipe Clamp" /></div>
              <div>
                <label style={S.label}>Family</label>
                <div style={{ display:'flex', gap:6 }}>
                  <select value={form.family} onChange={e => setForm(p=>({...p,family:e.target.value}))} style={{ ...S.input, flex:1 }}>
                    <option value="">Select family...</option>
                    {families.map(f => <option key={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div><label style={S.label}>Colour / Finish</label><input value={form.colour} onChange={e => setForm(p=>({...p,colour:e.target.value}))} style={S.input} placeholder="e.g. White, Brown, EG" /></div>
              <div style={{ gridColumn:'span 2' }}><label style={S.label}>Notes</label><input value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))} style={S.input} placeholder="Any additional notes" /></div>
            </div>
            <div style={{ display:'flex', gap:10, paddingTop:14, borderTop:'1px solid #e5e7eb' }}>
              <button onClick={save} disabled={saving||!form.code||!form.name} style={S.btnPrimary}>{saving?'Saving...':'Save product'}</button>
              <button onClick={() => setShowAdd(false)} style={S.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Family Manager Modal */}
      {showFamilyMgr && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:400 }}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>Product families</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:16 }}>Add, view or delete product families. Assign a production stage template to a family and it'll be suggested automatically on new orders for those products — still fully editable per order, this is just a starting point.</div>
            <div style={{ marginBottom:14 }}>
              {families.map(f => (
                <div key={f} style={{ padding:'8px 0', borderBottom:'1px solid #f3f4f6' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                    <span style={{ fontSize:13, fontWeight:500 }}>{f}</span>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <span style={{ fontSize:11, color:'#9ca3af' }}>{products.filter(p=>p.family===f).length} products</span>
                      <button onClick={() => deleteFamily(f)} style={{ ...S.btnSm, background:'#fef2f2', color:'#ef4444', padding:'3px 8px', fontSize:11 }}>Delete</button>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <select
                      value={familyTemplateChoice[f] ?? ''}
                      onChange={e => setFamilyTemplateChoice(c => ({ ...c, [f]: e.target.value }))}
                      style={{ ...S.input, flex:1, fontSize:12, padding:'4px 8px' }}
                    >
                      <option value="">No production template</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button
                      onClick={() => assignFamilyTemplate(f)}
                      disabled={assigningFamily === f}
                      style={{ ...S.btnSm, background:'#eff6ff', color:'#3b82f6', fontSize:11 }}
                    >{assigningFamily === f ? 'Applying...' : 'Apply to family'}</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              <input value={newFamily} onChange={e => setNewFamily(e.target.value)} onKeyDown={e => e.key==='Enter'&&addFamily()} placeholder="New family name..." style={{ ...S.input, flex:1 }} />
              <button onClick={addFamily} disabled={!newFamily.trim()} style={S.btnPrimary}>Add</button>
            </div>
            <button onClick={() => setShowFamilyMgr(false)} style={S.btnSecondary}>Close</button>
          </div>
        </div>
      )}

      {/* Template Manager Modal */}
      {showTemplateMgr && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:480, maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>Production stage templates</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:16 }}>
              These are the templates used for production stage suggestions in the Production tab.
              New ones can be created there ("Save these stages as a template") or assigned to a family above.
              Can't delete a template that's still assigned to a family — reassign or clear those first.
            </div>
            <div style={{ marginBottom:14 }}>
              {templates.map(t => (
                <div key={t.id} style={{ padding:'10px 0', borderBottom:'1px solid #f3f4f6' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{t.name}</div>
                      <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>{t.stages.length} stages: {t.stages.join(' → ')}</div>
                    </div>
                    <button
                      onClick={() => deleteTemplate(t)}
                      disabled={deletingTemplate === t.id}
                      style={{ ...S.btnSm, background:'#fef2f2', color:'#ef4444', padding:'3px 8px', fontSize:11, flexShrink:0 }}
                    >{deletingTemplate === t.id ? 'Deleting...' : 'Delete'}</button>
                  </div>
                </div>
              ))}
              {templates.length === 0 && <div style={{ fontSize:13, color:'#9ca3af', padding:'10px 0' }}>No templates yet.</div>}
            </div>
            <button onClick={() => setShowTemplateMgr(false)} style={S.btnSecondary}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
